import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { CalendarClock, Loader2, X } from 'lucide-react';
import './assetExport.css';
import './reviewPostponement.css';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const localDate = date => `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;

export const ReviewPostponeDialog = ({ item, onCancel, onSaved, endpoint }) => {
  const dialog = useRef(null);
  const noteInput = useRef(null);
  const operation = useRef(crypto.randomUUID());
  const inFlight = useRef(false);
  const [choice, setChoice] = useState('7');
  const [date, setDate] = useState('');
  const [note, setNote] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const tomorrow = new Date(); tomorrow.setDate(tomorrow.getDate() + 1);
  const calculated = new Date(); calculated.setDate(calculated.getDate() + Number(choice || 0));
  const selected = choice === 'date' ? date : localDate(calculated);
  useEffect(() => {
    const node = dialog.current; node.showModal(); noteInput.current?.focus();
    return () => node.close();
  }, []);
  const change = setter => event => { operation.current = crypto.randomUUID(); setter(event.target.value); setError(''); };
  const cancel = () => { if (!inFlight.current) onCancel(); };
  const submit = async event => {
    event.preventDefault();
    if (inFlight.current) return;
    if (!note.trim()) { setError('La nota explicativa es obligatoria.'); noteInput.current?.focus(); return; }
    if (!selected || selected < localDate(tomorrow)) { setError('Elige una fecha posterior a hoy.'); return; }
    inFlight.current = true; setBusy(true); setError('');
    try {
      const url = endpoint
        ? `${API}${endpoint}`
        : `${API}/me/pendientes-revision/${encodeURIComponent(item.audit_log_id || item.id)}/posponer`;
      await axios.post(url, {
        choice, on_date: choice === 'date' ? date : null, note: note.trim(),
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        expected_revision: item.postponement?.revision || 0, operation_id: operation.current,
      }, { withCredentials: true });
      window.dispatchEvent(new Event('xenicos:refresh-pendientes'));
      await onSaved();
    } catch (e) {
      const detail = e.response?.data?.detail;
      setError(typeof detail === 'string' ? detail : 'No se pudo posponer. Revisa los datos y vuelve a intentarlo.');
    } finally { inFlight.current = false; setBusy(false); }
  };
  return <dialog ref={dialog} className="asset-export-dialog review-postpone-dialog" aria-labelledby="postpone-title"
    data-testid="review-postpone-dialog" onCancel={event => { event.preventDefault(); cancel(); }}>
    <header className="flex justify-between items-start gap-3">
      <h2 id="postpone-title" className="text-lg font-semibold" data-testid="review-postpone-title">Posponer solicitud</h2>
      <button type="button" disabled={busy} onClick={cancel} title="Cerrar" aria-label="Cerrar"
        data-testid="review-postpone-close"><X size={20} /></button>
    </header>
    <p className="text-sm mt-3 [overflow-wrap:anywhere]" data-testid="review-postpone-context">{item.asset_codigo_npl || 'Solicitud de revisión'}</p>
    <p className="text-sm my-3" data-testid="review-postpone-shared-warning">La nueva fecha y la nota se aplicarán a todos los destinatarios.</p>
    <form onSubmit={submit} data-testid="review-postpone-form">
      <fieldset disabled={busy} className="space-y-4">
        <div className="review-postpone-choices" role="radiogroup" aria-label="Nuevo vencimiento">
          {[['7', '7 días'], ['15', '15 días'], ['date', 'Fecha concreta']].map(([value, label]) => <label key={value}>
            <input type="radio" name="postpone-choice" value={value} checked={choice === value}
              onChange={change(setChoice)} data-testid={`review-postpone-choice-${value}`} />{label}
          </label>)}
        </div>
        {choice === 'date' && <div><label htmlFor="postpone-calendar">Nueva fecha</label>
          <input id="postpone-calendar" type="date" value={date} min={localDate(tomorrow)}
            onChange={change(setDate)} className="input-field w-full mt-1" data-testid="review-postpone-date" /></div>}
        {selected && <p className="text-sm" data-testid="review-postpone-date-preview">Vencimiento: {new Date(`${selected}T00:00:00`).toLocaleDateString('es-ES')}</p>}
        <div><label htmlFor="postpone-note" className="text-sm font-medium">Nota explicativa <span aria-hidden="true">*</span></label>
          <textarea ref={noteInput} id="postpone-note" rows={4} maxLength={4000} value={note}
            onChange={change(setNote)} className="input-field w-full mt-1" data-testid="review-postpone-note" /></div>
      </fieldset>
      {error && <p role="alert" className="text-sm text-red-500 mt-3 [overflow-wrap:anywhere]" data-testid="review-postpone-error">{error}</p>}
      <footer className="flex flex-wrap justify-end gap-2 mt-5">
        <button type="button" disabled={busy} onClick={cancel} className="btn-secondary" data-testid="review-postpone-cancel">Cancelar</button>
        <button type="submit" disabled={busy} className="btn-primary inline-flex gap-2 items-center" data-testid="review-postpone-submit">
          {busy ? <Loader2 className="animate-spin" size={16} /> : <CalendarClock size={16} />}{busy ? 'Guardando…' : 'Posponer'}</button>
      </footer>
    </form>
  </dialog>;
};